<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-[100000] bg-white dark:bg-black flex flex-col landscape:flex-row portrait:overflow-y-auto landscape:overflow-hidden">
      <!-- Portrait: Image Container (scrollable with content) -->
      <div
        class="portrait:w-full portrait:flex portrait:items-center portrait:justify-center portrait:p-2 portrait:bg-black landscape:flex-1 landscape:flex landscape:items-center landscape:justify-center landscape:p-4 landscape:bg-black">
        <canvas
          ref="canvasRef"
          class="w-full object-contain rounded-lg shadow-2xl"></canvas>
      </div>

      <!-- Close button -->
      <button
        @click="$emit('close')"
        class="fixed landscape:top-4 landscape:left-4 portrait:top-4 portrait:left-4 z-[100001] camera-btn camera-btn--medium camera-btn--close">
        <X class="w-5 h-5" />
      </button>

      <!-- Info button -->
      <button
        v-if="ui.showInfo"
        @click="toggleInfo"
        class="fixed landscape:bottom-4 landscape:right-[420px] portrait:bottom-4 portrait:right-4 z-[100001] camera-btn camera-btn--medium camera-btn--close">
        <Info class="w-5 h-5" />
      </button>

      <!-- Info Popup -->
      <div
        v-if="showInfoPopup"
        class="fixed landscape:bottom-20 landscape:right-[420px] portrait:bottom-20 portrait:right-4 z-[100001] w-64 bg-[var(--color-primary-100)] border border-[var(--color-primary-400)] rounded-xl shadow-xl p-4 text-sm text-[var(--color-text)] backdrop-blur-md">
        <div class="flex justify-between items-center mb-2">
          <span class="font-semibold text-[var(--color-primary-900)]">
            {{ $t('views.segmentation.title') }}
          </span>
          <X class="w-4 h-4 cursor-pointer" @click="showInfoPopup = false" />
        </div>
        <p class="leading-snug">{{ $t('pageInfo.segmentationResult') }}</p>
      </div>

      <!-- Results Panel -->
      <div
        class="landscape:relative landscape:w-[400px] landscape:flex-shrink-0 landscape:overflow-y-auto bg-[var(--color-primary-100)] p-6 landscape:p-4 portrait:border-t-2 landscape:border-l-2 border-[var(--color-primary-400)]">
        <!-- Weight Result -->
        <div class="mb-6 relative">
          <div
            class="text-sm text-[var(--color-text)] opacity-70 mb-2 text-center">
            {{ $t('views.segmentation.title') }}
          </div>
          <div class="flex items-center justify-center gap-8 mb-2">
            <button
              @click="$emit('retry')"
              class="p-3 rounded-lg bg-[var(--color-primary-300)] text-[var(--color-text)] hover:bg-[var(--color-primary-400)] transition border border-[var(--color-primary-400)]">
              <RotateCcw class="w-6 h-6" />
            </button>
            <div class="text-5xl font-bold text-[var(--color-text)]">
              {{ weight?.toFixed(1) }}
              <span class="text-3xl opacity-70">kg</span>
            </div>
            <button
              @click="handleNext"
              :disabled="!canSave"
              class="p-3 rounded-lg bg-[var(--color-highlight)] text-white transition"
              :class="{
                'opacity-50 cursor-not-allowed': !canSave,
                'hover:opacity-90': canSave,
              }">
              <ArrowRight class="w-6 h-6" />
            </button>
          </div>
          <div class="flex items-center justify-center gap-2 mb-2">
            <div class="text-sm text-[var(--color-text)] opacity-70">
              {{ $t('views.segmentation.accuracy') }}
            </div>
            <div
              class="px-3 py-1 rounded-full text-sm font-semibold"
              :style="{
                backgroundColor: accuracyColor,
                color: accuracyTextColor,
              }">
              {{ (calculatedAccuracy * 100)?.toFixed(0) }}%
            </div>
            <MiniTooltip :warnings="accuracyWarnings" />
          </div>
        </div>

        <!-- Measurements Grid -->
        <div v-if="measurements" class="grid grid-cols-2 gap-3 mb-4">
          <div class="info-card">
            <div class="flex items-center justify-between mb-1">
              <div class="info-card__label">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full mr-0.5 mb-1 align-middle"
                  :style="{ backgroundColor: MEASUREMENT_COLORS.bodyLength }" />
                {{ $t('views.segmentation.bodyLength') }}
              </div>
              <MiniTooltip :text="$t('views.segmentation.info.bodyLength')" />
            </div>
            <div class="info-card__value">
              {{ measurements.lengthCm?.toFixed(1) }} cm
            </div>
          </div>
          <div class="info-card">
            <div class="flex items-center justify-between mb-1">
              <div class="info-card__label">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full mr-0.5 mb-1 align-middle"
                  :style="{ backgroundColor: MEASUREMENT_COLORS.bodyHeight }" />
                {{ $t('views.segmentation.bodyHeight') }}
              </div>
              <MiniTooltip :text="$t('views.segmentation.info.bodyHeight')" />
            </div>
            <div class="info-card__value">
              {{ measurements.heightCm?.toFixed(1) }} cm
            </div>
          </div>
          <div class="info-card">
            <div class="flex items-center justify-between mb-1">
              <div class="info-card__label">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full mr-0.5 mb-1 align-middle"
                  :style="{
                    backgroundColor: MEASUREMENT_COLORS.shoulderHeight,
                  }" />
                {{ $t('views.segmentation.shoulderHeight') }}
              </div>
              <MiniTooltip
                :text="$t('views.segmentation.info.shoulderHeight')" />
            </div>
            <div class="info-card__value">
              {{ measurements.shoulderCm?.toFixed(1) }} cm
            </div>
          </div>
          <div class="info-card">
            <div class="flex items-center justify-between mb-1">
              <div class="info-card__label">
                <span
                  class="inline-block w-2.5 h-2.5 rounded-full mr-0.5 mb-1 align-middle"
                  :style="{ backgroundColor: MEASUREMENT_COLORS.rumpHeight }" />
                {{ $t('views.segmentation.rumpHeight') }}
              </div>
              <MiniTooltip :text="$t('views.segmentation.info.rumpHeight')" />
            </div>
            <div class="info-card__value">
              {{ measurements.rumpCm?.toFixed(1) }} cm
            </div>
          </div>
        </div>

        <!-- Editable Parameters -->
        <div
          class="mb-4 p-3 bg-[var(--color-primary-200)] rounded-lg space-y-2 border border-[var(--color-primary-400)]">
          <div
            class="text-sm font-semibold text-[var(--color-text)] mb-2 opacity-70">
            {{ $t('views.segmentation.adjustParameters') }}
          </div>

          <!-- Direction -->
          <div class="flex items-center gap-2">
            <label class="label w-20">{{
              $t('views.segmentation.direction')
            }}</label>
            <div class="flex gap-2 flex-1">
              <button
                @click="editableDirection = 'left'"
                :class="[
                  'flex-1 py-2 px-3 rounded-lg font-medium text-base transition flex items-center justify-center',
                  editableDirection === 'left'
                    ? 'bg-[var(--color-highlight)] text-white'
                    : 'bg-[var(--color-primary-100)] text-[var(--color-text)] border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)]',
                ]">
                <ArrowLeft class="w-5 h-5" />
              </button>
              <button
                @click="editableDirection = 'right'"
                :class="[
                  'flex-1 py-2 px-3 rounded-lg font-medium text-base transition flex items-center justify-center',
                  editableDirection === 'right'
                    ? 'bg-[var(--color-highlight)] text-white'
                    : 'bg-[var(--color-primary-100)] text-[var(--color-text)] border border-[var(--color-primary-400)] hover:bg-[var(--color-primary-300)]',
                ]">
                <ArrowRight class="w-5 h-5" />
              </button>
            </div>
            <span class="label w-6"></span>
          </div>

          <!-- Distance -->
          <div class="flex items-center gap-2">
            <label class="label w-20">{{
              $t('views.segmentation.distance')
            }}</label>
            <input
              v-model.number="editableDistance"
              type="number"
              step="0.1"
              min="0.5"
              max="10"
              class="flex-1 px-2 py-1.5 text-base rounded bg-[var(--color-primary-100)] text-[var(--color-text)] border border-[var(--color-primary-400)]" />
            <span class="label w-6">m</span>
          </div>

          <!-- Sensor Settings -->
          <div class="space-y-2">
            <div
              class="text-sm font-semibold text-[var(--color-text)] mt-4 mb-2 opacity-70">
              {{ $t('views.more.focalLength.title') }}
            </div>

            <div class="flex items-center gap-2">
              <label class="label w-20">{{
                $t('views.more.focalLength.focalLengthLabel')
              }}</label>
              <input
                v-model.number="editableFocalLength"
                type="number"
                step="0.01"
                min="1"
                max="100"
                class="flex-1 px-2 py-1.5 text-base rounded bg-[var(--color-primary-100)] text-[var(--color-text)] border border-[var(--color-primary-400)]"
                :placeholder="
                  debugInfo?.focalLengthUsed
                    ? `${debugInfo.focalLengthUsed.toFixed(2)}`
                    : '4.2'
                " />
              <span class="label w-6">mm</span>
            </div>

            <div class="flex items-center gap-2">
              <label class="label w-20">{{
                $t('views.more.focalLength.focal35mmLabel')
              }}</label>
              <input
                v-model.number="editableFocal35mm"
                type="number"
                step="0.1"
                min="1"
                max="500"
                class="flex-1 px-2 py-1.5 text-base rounded bg-[var(--color-primary-100)] text-[var(--color-text)] border border-[var(--color-primary-400)]"
                :placeholder="
                  debugInfo?.exifData?.focal35mm
                    ? `${debugInfo.exifData.focal35mm.toFixed(0)}`
                    : '26'
                " />
              <span class="label w-6">mm</span>
            </div>

            <div
              class="flex start gap-2 items-center text-sm pt-1 border-t border-[var(--color-primary-400)]">
              <span class="label">{{
                $t('views.more.focalLength.sensorSizeLabel')
              }}</span>
              <span class="label font-semibold">
                {{ calculatedSensorSize.toFixed(2) }} mm
              </span>
            </div>
          </div>

          <button
            v-if="hasParameterChanges"
            @click="recalculate"
            class="w-full py-2 text-base rounded-lg font-semibold bg-[var(--color-highlight)] text-white hover:opacity-90 transition">
            {{ $t('views.segmentation.recalculate') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
  import { ref, watch, nextTick, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { X, ArrowLeft, ArrowRight, RotateCcw, Info } from 'lucide-vue-next'
  import { useUiStore } from '@/stores/ui.store'
  import MiniTooltip from '@/components/ui/MiniTooltip.vue'

  const { t } = useI18n()
  const ui = useUiStore()
  const showInfoPopup = ref(false)
  const toggleInfo = () => (showInfoPopup.value = !showInfoPopup.value)

  const props = defineProps({
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'Segmentation Result',
    },
    photoData: {
      type: String,
      required: false,
      default: null,
    },
    segmentation: {
      type: Object,
      default: null,
    },
    measurements: {
      type: Object,
      default: null,
    },
    metrics: {
      type: Object,
      default: null,
    },
    weight: {
      type: Number,
      default: 0,
    },
    accuracy: {
      type: Number,
      default: 0,
    },
    debugInfo: {
      type: Object,
      default: null,
    },
  })

  const emit = defineEmits(['close', 'retry', 'recalculate', 'next'])

  function handleNext() {
    emit('next', {
      weight: props.weight,
    })
  }

  const canvasRef = ref(null)
  const canSave = computed(() => true)

  const MEASUREMENT_COLORS = {
    bodyLength: '#22c55e', // green
    bodyHeight: '#eab308', // yellow
    shoulderHeight: '#3b82f6', // blue
    rumpHeight: '#f97316', // orange
  }

  // Editable parameters
  const editableDirection = ref(props.debugInfo?.direction || 'left')
  const editableDistance = ref(props.debugInfo?.distance || 1.5)
  const editableFocalLength = ref(props.debugInfo?.focalLengthUsed || null)
  const editableFocal35mm = ref(props.debugInfo?.exifData?.focal35mm || null)

  const calculatedSensorSize = computed(() => {
    const f =
      editableFocalLength.value || props.debugInfo?.focalLengthUsed || 4.2
    const f35 =
      editableFocal35mm.value || props.debugInfo?.exifData?.focal35mm || 26.0
    if (f > 0 && f35 > 0) {
      const diagonal = (43.27 * f) / f35
      return (4 * diagonal) / 5
    }
    return 4.2
  })

  // Check if parameters have changed
  const hasParameterChanges = computed(() => {
    return (
      editableDirection.value !== (props.debugInfo?.direction || 'left') ||
      editableDistance.value !== (props.debugInfo?.distance || 1.5) ||
      (editableFocalLength.value &&
        editableFocalLength.value !==
          (props.debugInfo?.focalLengthUsed || null)) ||
      (editableFocal35mm.value &&
        editableFocal35mm.value !==
          (props.debugInfo?.exifData?.focal35mm || null))
    )
  })

  // Recalculate with new parameters
  function recalculate() {
    emit('recalculate', {
      direction: editableDirection.value,
      distance: editableDistance.value,
      focalLength: editableFocalLength.value,
      focal35mm: editableFocal35mm.value,
    })
  }

  // Compute accuracy warnings based on measurements and debug info
  const accuracyWarnings = computed(() => {
    const warnings = []
    const w = props.weight || 0
    const m = props.measurements
    const debug = props.debugInfo

    // Weight range check (only show warnings)
    if (w < 10 || w > 120) {
      warnings.push({
        passed: false,
        message: t('views.segmentation.warnings.weightOutsideNormal'),
      })
    } else if (w < 20 || w > 100) {
      warnings.push({
        passed: false,
        message: t('views.segmentation.warnings.weightNearEdge'),
      })
    }

    // Body measurements check (only show warnings)
    if (m) {
      if (m.lengthCm < 40 || m.lengthCm > 120) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.bodyLengthUnusual'),
        })
      }

      if (m.heightCm < 30 || m.heightCm > 100) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.bodyHeightUnusual'),
        })
      }

      // Check proportions
      const aspectRatio = m.lengthCm / m.heightCm
      if (aspectRatio < 1.2 || aspectRatio > 2.5) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.unusualProportions'),
        })
      }
    }

    // EXIF/focal length check (show focal length source info)
    if (debug) {
      if (debug.focalLengthSource === 'exif') {
        warnings.push({
          passed: true,
          message: t('views.segmentation.warnings.focalLengthFromExif'),
        })
      } else if (debug.focalLengthSource === 'user-override') {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.focalLengthUserOverride'),
        })
      } else {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.usingDefaultFocal'),
        })
      }
    }

    // Distance check (only show warnings)
    if (debug && debug.distance) {
      if (debug.distance < 0.5 || debug.distance > 5.0) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.distanceOutsideOptimal'),
        })
      } else if (debug.distance < 1.0) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.distanceLessThan1m'),
        })
      }
    }

    // Viewing angle check (always show warning)
    if (debug && debug.viewingAngle !== undefined) {
      // Optimal viewing angle: 70-110 degrees (20 degrees down to 20 degrees up from horizontal)
      // Default is 90 degrees (horizontal/perpendicular to ground)
      if (debug.viewingAngle < 60 || debug.viewingAngle > 120) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.viewingAngleExtreme'),
        })
      } else if (debug.viewingAngle < 70 || debug.viewingAngle > 110) {
        warnings.push({
          passed: false,
          message: t('views.segmentation.warnings.viewingAngleSuboptimal'),
        })
      } else {
        warnings.push({
          passed: true,
          message: t('views.segmentation.warnings.viewingAngleOptimal'),
        })
      }
    }

    return warnings
  })

  // Calculate actual accuracy based on warnings
  const calculatedAccuracy = computed(() => {
    const warnings = accuracyWarnings.value

    // Start with base accuracy from props (already includes focal length penalty from WeightPipeline)
    let baseAccuracy = props.accuracy || 0

    // If no warnings, accuracy is just the base segmentation quality
    if (warnings.length === 0) return baseAccuracy

    // Apply small penalties for actual measurement/data issues (not focal length which is handled in pipeline)
    let accuracy = baseAccuracy

    warnings.forEach((w) => {
      // Only penalize for actual measurement issues, not focal length source info
      // Focal length penalty is already applied in WeightPipeline
      if (!w.passed && !w.message.toLowerCase().includes('focal')) {
        accuracy *= 0.95 // 5% penalty per measurement issue
      }
    })

    // Return accuracy with penalties applied, minimum 0
    return Math.max(0, accuracy)
  })

  // Compute accuracy color based on calculated percentage
  const accuracyColor = computed(() => {
    const percent = calculatedAccuracy.value * 100
    if (percent >= 80) return '#16a34a' // Green
    if (percent >= 60) return '#eab308' // Yellow
    if (percent >= 40) return '#f97316' // Orange
    return '#dc2626' // Red
  })

  const accuracyTextColor = computed(() => {
    return '#ffffff'
  })

  // Watch for changes and redraw
  watch(
    () => [props.show, props.photoData, props.segmentation],
    async () => {
      if (props.show && props.photoData) {
        await nextTick()
        drawVisualization()
      }
    },
    { immediate: true }
  )

  // Reset editable parameters when debug info changes
  watch(
    () => props.debugInfo,
    (newDebug) => {
      if (newDebug) {
        editableDirection.value = newDebug.direction || 'left'
        editableDistance.value = newDebug.distance || 1.5
        editableFocalLength.value = newDebug.focalLengthUsed || null
        editableFocal35mm.value = newDebug.exifData?.focal35mm || null
      }
    },
    { immediate: true }
  )

  async function drawVisualization() {
    if (!canvasRef.value || !props.photoData) return

    const canvas = canvasRef.value
    const ctx = canvas.getContext('2d')

    // Load original image
    const img = new Image()
    img.src = props.photoData

    await new Promise((resolve) => {
      img.onload = resolve
    })

    // Set canvas size to match image
    canvas.width = img.width
    canvas.height = img.height

    // Draw original image
    ctx.drawImage(img, 0, 0)

    // If segmentation data is available, overlay the mask
    if (
      props.segmentation &&
      (props.segmentation.mask || props.segmentation.maskData)
    ) {
      drawSegmentationOverlay(ctx, img.width, img.height)
    }
  }

  function drawSegmentationOverlay(ctx, imgWidth, imgHeight) {
    if (!props.segmentation) return
    const {
      maskData,
      mask,
      maskWidth,
      maskHeight,
      boundingBox,
      scaledWidth,
      scaledHeight,
      originalWidth,
      originalHeight,
    } = props.segmentation

    const resolvedMaskData = maskData || mask
    if (!resolvedMaskData) return

    /**
     * COORDINATE TRANSFORMATION LOGIC (Normalized for any aspect ratio)
     *
     * Mapping mask coordinates (max 640px) to the actual canvas display dimensions.
     * Works for square, landscape (4:3) and portrait (3:4) images.
     */

    // 1. Identify dimensions of the source image that the mask refers to
    const origW =
      originalWidth ||
      (scaledWidth ? (scaledWidth / maskWidth) * maskWidth : maskWidth)
    const origH =
      originalHeight ||
      (scaledHeight ? (scaledHeight / maskHeight) * maskHeight : maskHeight)

    // 2. Identify relationship between ML source and Display image
    const mlAR = origW / origH
    const displayAR = imgWidth / imgHeight

    // Determine what part of the ML image (and thus the mask) is shown in the display canvas
    let srcX = 0,
      srcY = 0,
      srcW = maskWidth,
      srcH = maskHeight

    // If display is a square crop of a wider landscape image
    if (displayAR < mlAR - 0.01) {
      // The square is centered horizontally in the wider original image
      // We calculate how many mask pixels correspond to that centered square
      const squareSizeInOrig = Math.min(origW, origH)
      const horizontalScale = maskWidth / origW
      srcW = squareSizeInOrig * horizontalScale
      srcH = maskHeight // Use full mask height
      srcX = (maskWidth - srcW) / 2
      srcY = 0
    }
    // If display is a square crop of a taller portrait image
    else if (displayAR > mlAR + 0.01) {
      // The square is centered vertically in the taller original image
      const squareSizeInOrig = Math.min(origW, origH)
      const verticalScale = maskHeight / origH
      srcW = maskWidth // Use full mask width
      srcH = squareSizeInOrig * verticalScale
      srcX = 0
      srcY = (maskHeight - srcH) / 2
    }

    const scaleX = imgWidth / srcW
    const scaleY = imgHeight / srcH

    // 3. Prepare a mask canvas at native ML resolution
    const maskCanvas = document.createElement('canvas')
    maskCanvas.width = maskWidth
    maskCanvas.height = maskHeight
    const maskCtx = maskCanvas.getContext('2d')

    // 4. Convert binary Uint8Array into visual pixel data
    const imageData = maskCtx.createImageData(maskWidth, maskHeight)

    for (let i = 0; i < resolvedMaskData.length; i++) {
      const pixelIndex = i * 4
      const maskValue = resolvedMaskData[i]

      if (maskValue > 0) {
        imageData.data[pixelIndex] = 0 // R
        imageData.data[pixelIndex + 1] = 0 // G
        imageData.data[pixelIndex + 2] = 255 // B
        imageData.data[pixelIndex + 3] = 255 // Alpha
      } else {
        imageData.data[pixelIndex + 3] = 0 // Transparent
      }
    }

    maskCtx.putImageData(imageData, 0, 0)

    // 5. Draw the mapped part of the mask
    ctx.globalAlpha = 0.4
    ctx.drawImage(
      maskCanvas,
      srcX,
      srcY,
      srcW,
      srcH, // Source rectangle (logical crop)
      0,
      0,
      imgWidth,
      imgHeight // Destination (display area)
    )
    ctx.globalAlpha = 1.0

    // 5b. Draw measurement markers (lines + endpoint dots)
    const points = props.metrics?.points
    if (points) {
      const lineWidth = Math.max(3, Math.round(imgWidth / 350))
      const dotRadius = Math.max(4, Math.round(imgWidth / 140))

      const toDisplay = (x, y) => ({
        x: (x - srcX) * scaleX,
        y: (y - srcY) * scaleY,
      })

      const drawLineWithDots = (p, color) => {
        if (!p) return

        const a = toDisplay(p.x1, p.y1)
        const b = toDisplay(p.x2, p.y2)

        ctx.save()
        ctx.lineWidth = lineWidth
        ctx.strokeStyle = color
        ctx.globalAlpha = 0.95
        ctx.beginPath()
        ctx.moveTo(a.x, a.y)
        ctx.lineTo(b.x, b.y)
        ctx.stroke()

        ctx.fillStyle = color
        ctx.beginPath()
        ctx.arc(a.x, a.y, dotRadius, 0, Math.PI * 2)
        ctx.fill()
        ctx.beginPath()
        ctx.arc(b.x, b.y, dotRadius, 0, Math.PI * 2)
        ctx.fill()

        ctx.restore()
      }

      drawLineWithDots(points.bodyLength, MEASUREMENT_COLORS.bodyLength)
      drawLineWithDots(points.bodyHeight, MEASUREMENT_COLORS.bodyHeight)
      drawLineWithDots(points.shoulderHeight, MEASUREMENT_COLORS.shoulderHeight)
      drawLineWithDots(points.rumpHeight, MEASUREMENT_COLORS.rumpHeight)
    }

    // 6. Transform Bounding Box coordinates
    if (boundingBox) {
      // Scale from the portion of the mask we are viewing to the display area
      const bW =
        typeof boundingBox.width === 'function'
          ? boundingBox.width()
          : boundingBox.w || boundingBox.width || 0
      const bH =
        typeof boundingBox.height === 'function'
          ? boundingBox.height()
          : boundingBox.h || boundingBox.height || 0
      const centerX = typeof boundingBox.x === 'number' ? boundingBox.x : 0
      const centerY = typeof boundingBox.y === 'number' ? boundingBox.y : 0

      // Calculate top-left corner
      const topX =
        typeof boundingBox.topX === 'function'
          ? boundingBox.topX()
          : typeof boundingBox.topX === 'number'
          ? boundingBox.topX
          : centerX - bW / 2
      const topY =
        typeof boundingBox.topY === 'function'
          ? boundingBox.topY()
          : typeof boundingBox.topY === 'number'
          ? boundingBox.topY
          : centerY - bH / 2

      // Mapping: (Mask Coordinate - Viewport Offset) * Transformation Scale
      const displayX = (topX - srcX) * scaleX
      const displayY = (topY - srcY) * scaleY
      const displayW = bW * scaleX
      const displayH = bH * scaleY

      const lineWidth = Math.max(4, Math.round((imgWidth / 1000) * 2))

      ctx.strokeStyle = '#e61637' // Digiziege Red
      ctx.lineWidth = lineWidth
      ctx.strokeRect(displayX, displayY, displayW, displayH)
    }
  }
</script>

<style scoped>
  .camera-btn {
    background: var(--color-primary-100);
    backdrop-filter: blur(10px);
    border: 2px solid var(--color-primary-400);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, background 0.2s;
    color: var(--color-text);
  }

  .camera-btn:hover {
    background: var(--color-primary-200);
    transform: scale(1.05);
  }

  .camera-btn:active {
    transform: scale(0.95);
  }

  .camera-btn--medium {
    width: 48px;
    height: 48px;
    border-radius: 12px;
  }

  .camera-btn--close {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
